import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  ASSIGNED_ITEM_MARKING_TASK_OPTIONS,
  BLACKOUT_DOW_PROPS,
  BlackoutDowProp,
  IDisallowedTimeRange,
  applyDowPreset,
  describeRange,
  describeRangeSchedule,
  isDateScopedRange,
  isOvernightRange,
  newDateRange,
  newRecurringRange,
  parseMarkingTasks,
  ruleDisplayTitle,
  validateRange,
} from '../types';

/**
 * DELIV-17370 — rule-row editor for a non-working (blackout) hours group.
 *
 * Renders and mutates the `ranges` array in place; the host owns loading and saving.
 * Used by the Test Controller scoring window panel and the Scoring Leader
 * System Settings groups page, so it holds no API calls of its own.
 */
@Component({
  selector: 'blackout-hours-editor',
  templateUrl: './blackout-hours-editor.component.html',
  styleUrls: ['./blackout-hours-editor.component.scss']
})
export class BlackoutHoursEditorComponent implements OnChanges {

  /** Rule rows being edited; mutated in place and echoed through `rangesChange`. */
  @Input() ranges: IDisallowedTimeRange[] = [];
  /** Group timezone, shown so the editor's times are unambiguous. */
  @Input() timezone: string;
  @Input() disabled: boolean = false;

  @Output() rangesChange = new EventEmitter<IDisallowedTimeRange[]>();

  /** Rule indices with the date-range editor expanded (weekly rules stay collapsed until opened). */
  private dateSectionExpandedIndices = new Set<number>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ranges) {
      if (!Array.isArray(this.ranges)) {
        this.ranges = [];
      }
      this.expandedRuleIndices.clear();
      this.dateSectionExpandedIndices.clear();
    }
  }

  BLACKOUT_DOW_PROPS = BLACKOUT_DOW_PROPS;
  markingTaskOptions = ASSIGNED_ITEM_MARKING_TASK_OPTIONS;
  describeRange = describeRange;
  describeRangeSchedule = describeRangeSchedule;
  ruleDisplayTitle = ruleDisplayTitle;
  isDateScopedRange = isDateScopedRange;
  isOvernightRange = isOvernightRange;
  validateRange = validateRange;

  /** Rule indices showing the full edit form; others show summary only until Edit / Done. */
  private expandedRuleIndices = new Set<number>();

  isExpanded(index: number): boolean {
    return this.expandedRuleIndices.has(index);
  }

  expandRule(index: number, event?: Event) {
    event?.stopPropagation();
    if (this.disabled) return;
    this.expandedRuleIndices.add(index);
  }

  /** Collapse after the rule passes validation; stay open and show the error otherwise. */
  doneEditing(index: number, event?: Event) {
    event?.stopPropagation();
    if (this.disabled) return;
    if (validateRange(this.ranges[index])) return;
    this.expandedRuleIndices.delete(index);
  }

  private markExpanded(index: number) {
    this.expandedRuleIndices.add(index);
  }

  private syncExpandedAfterRemove(removedIndex: number) {
    const next = new Set<number>();
    for (const i of this.expandedRuleIndices) {
      if (i < removedIndex) next.add(i);
      else if (i > removedIndex) next.add(i - 1);
    }
    this.expandedRuleIndices = next;
    this.syncDateSectionAfterRemove(removedIndex);
  }

  private syncExpandedAfterInsert(insertIndex: number) {
    const next = new Set<number>();
    for (const i of this.expandedRuleIndices) {
      next.add(i >= insertIndex ? i + 1 : i);
    }
    next.add(insertIndex);
    this.expandedRuleIndices = next;
    this.syncDateSectionAfterInsert(insertIndex);
  }

  private syncDateSectionAfterRemove(removedIndex: number) {
    const next = new Set<number>();
    for (const i of this.dateSectionExpandedIndices) {
      if (i < removedIndex) next.add(i);
      else if (i > removedIndex) next.add(i - 1);
    }
    this.dateSectionExpandedIndices = next;
  }

  private syncDateSectionAfterInsert(insertIndex: number) {
    const next = new Set<number>();
    for (const i of this.dateSectionExpandedIndices) {
      next.add(i >= insertIndex ? i + 1 : i);
    }
    this.dateSectionExpandedIndices = next;
  }

  hasDateValues(range: IDisallowedTimeRange): boolean {
    return !!(range?.date_start || range?.date_end);
  }

  isDateSectionOpen(range: IDisallowedTimeRange, index: number): boolean {
    return this.hasDateValues(range) || this.dateSectionExpandedIndices.has(index);
  }

  openDateSection(index: number) {
    if (this.disabled) return;
    this.dateSectionExpandedIndices.add(index);
  }

  hideDateSection(index: number) {
    this.dateSectionExpandedIndices.delete(index);
  }

  clearDates(range: IDisallowedTimeRange, index: number) {
    if (this.disabled) return;
    this.replaceRange(range, { ...range, date_start: null, date_end: null });
    this.dateSectionExpandedIndices.delete(index);
  }

  private emit() {
    this.rangesChange.emit(this.ranges);
  }

  addRecurringRule() {
    if (this.disabled) return;
    if (!Array.isArray(this.ranges)) this.ranges = [];
    this.ranges.push(newRecurringRange());
    this.markExpanded(this.ranges.length - 1);
    this.emit();
  }

  addDateRule() {
    if (this.disabled) return;
    if (!Array.isArray(this.ranges)) this.ranges = [];
    this.ranges.push(newDateRange(false));
    const index = this.ranges.length - 1;
    this.markExpanded(index);
    this.openDateSection(index);
    this.emit();
  }

  /** Copy a rule so a variant can be edited without retyping the whole row. */
  duplicateRule(index: number) {
    if (this.disabled) return;
    const source = this.ranges[index];
    const copy = { ...source, marking_tasks: parseMarkingTasks(source.marking_tasks) };
    delete copy.id;
    const insertIndex = index + 1;
    this.ranges.splice(insertIndex, 0, copy);
    this.syncExpandedAfterInsert(insertIndex);
    this.emit();
  }

  removeRule(index: number) {
    if (this.disabled) return;
    if (!Array.isArray(this.ranges)) return;
    this.ranges.splice(index, 1);
    this.syncExpandedAfterRemove(index);
    this.emit();
  }

  isDowChecked(range: IDisallowedTimeRange, prop: BlackoutDowProp): boolean {
    return Number(range[prop]) === 1;
  }

  setDowChecked(range: IDisallowedTimeRange, prop: BlackoutDowProp, checked: boolean) {
    if (this.disabled) return;
    this.replaceRange(range, { ...range, [prop]: checked ? 1 : 0 });
  }

  setWeekdayPreset(range: IDisallowedTimeRange, preset: 'weekdays' | 'weekend' | 'all' | 'none') {
    if (this.disabled) return;
    this.replaceRange(range, applyDowPreset(range, preset));
  }

  setRuleName(range: IDisallowedTimeRange, raw: string) {
    if (this.disabled) return;
    const name = raw != null ? String(raw).trim() : '';
    this.replaceRange(range, { ...range, name: name || null });
  }

  hasRuleName(range: IDisallowedTimeRange): boolean {
    return !!(range?.name != null && String(range.name).trim());
  }

  /** Swap a rule row so ngModel-bound checkboxes pick up programmatic DOW changes. */
  private replaceRange(current: IDisallowedTimeRange, next: IDisallowedTimeRange) {
    const index = this.ranges.indexOf(current);
    if (index === -1) return;
    this.ranges[index] = next;
    this.emit();
  }

  isTaskChecked(range: IDisallowedTimeRange, taskType: string): boolean {
    return parseMarkingTasks(range.marking_tasks).includes(taskType);
  }

  toggleTask(range: IDisallowedTimeRange, taskType: string) {
    if (this.disabled) return;
    const tasks = parseMarkingTasks(range.marking_tasks);
    const index = tasks.indexOf(taskType);
    if (index === -1) tasks.push(taskType);
    else tasks.splice(index, 1);
    range.marking_tasks = tasks;
    this.emit();
  }

  isAllowAllDay(range: IDisallowedTimeRange): boolean {
    return Number(range.is_allow_all_day) === 1;
  }

  /** True when a blackout rule has no clock span and therefore covers the whole day. */
  isFullDayBlackout(range: IDisallowedTimeRange): boolean {
    if (this.isAllowAllDay(range)) return false;
    if (!range.time_start || !range.time_end) return true;
    return range.time_start === range.time_end;
  }

  onTimeChange(range: IDisallowedTimeRange, field: 'time_start' | 'time_end', value: string) {
    if (this.disabled) return;
    this.replaceRange(range, { ...range, [field]: value });
  }

  onDateStartChange(range: IDisallowedTimeRange, index: number, value: string | null) {
    if (this.disabled) return;
    this.openDateSection(index);
    this.replaceRange(range, { ...range, date_start: value || null });
  }

  onDateEndChange(range: IDisallowedTimeRange, index: number, value: string | null) {
    if (this.disabled) return;
    this.openDateSection(index);
    this.replaceRange(range, { ...range, date_end: value || null });
  }

  trackByIndex(index: number): number {
    return index;
  }
}
